import type {ReactElement} from 'react';
import React from 'react';
import {HexPicker} from '@Components/color-picker-v2/components/hex-picker';
import {PickerInputField} from '@Components/color-picker-v2/components/picker-input-field';
import {OnMyDesignSection} from '@Components/color-picker-v2/components/on-my-design-section';
import styles from './color-picker-popup.module.scss';
import {EyedropperColorPicker} from '@Components/eyedropper-color-picker/eyedropper-color-picker';
import type {RGBA} from '@Utils/color.util';
import {hextToHexString} from '@Utils/color.util';
import {hexToRgba, rgbaToHex} from '@Utils/color.util';

export interface ColorPickerPopupProps {
  containerClasses?: string;
  color: string;
  onMyDesign?: string[];

  onChange(color: string): void;

  onClickAdd?(): void;

  onChangeEnded(color: string): void;

  hideHexPicker?: boolean;
  hideHexInputField?: boolean;
  isMobileVersion?: boolean;
  isAlphaDisabled?: boolean;
  hideEyeDropperPicker?: boolean;
}

export function ColorPickerPopup({
  containerClasses = '',
  onClickAdd = (): void => {},
  hideHexPicker = false,
  isMobileVersion = false,
  hideHexInputField = false,
  isAlphaDisabled = false,
  hideEyeDropperPicker = true,
  ...props
}: ColorPickerPopupProps): ReactElement | null {
  const getHexPicker = (): ReactElement | null => {
    if (hideHexPicker) {
      return null;
    }

    return (
      <HexPicker
        className="spacing-m-b-3"
        color={hexToRgba(props.color)}
        onChange={(val: RGBA): void => {
          props.onChange(hextToHexString(rgbaToHex(val, !isAlphaDisabled)));
        }}
        onChangeEnded={(val: RGBA): void => {
          props.onChangeEnded(hextToHexString(rgbaToHex(val, !isAlphaDisabled)));
        }}
        isAlphaDisabled={isAlphaDisabled}
      />
    );
  };

  const getHexInputField = (): ReactElement | null => {
    if (hideHexInputField) {
      return null;
    }

    return (
      <div className={`${styles.inputFieldWrapper} _full-width`}>
        <PickerInputField
          color={props.color}
          onChangeColor={(val: string): void => {
            props.onChange(val);
          }}
          onChangeEnded={(val: string): void => {
            props.onChangeEnded(val);
          }}
          isMobileVersion={isMobileVersion}
          isAlphaDisabled={isAlphaDisabled}
        />
      </div>
    );
  };

  const getEyeDropperPicker = (): ReactElement | null => {
    if (hideEyeDropperPicker) {
      return null;
    }

    return (
      <EyedropperColorPicker
        onColorPicked={(val: string): void => {
          props.onChangeEnded(val);
        }}
      />
    );
  };

  const getOnMyDesignSection = (): ReactElement | null => {
    if (!props.onMyDesign) {
      return null;
    }

    return (
      <OnMyDesignSection
        onColorSelect={(color: string): void => {
          props.onChange(color);
          props.onChangeEnded(color);
        }}
        colorsOnDesign={props.onMyDesign}
        isMobileVersion={isMobileVersion}
        onClickAdd={(): void => {
          onClickAdd();
        }}
        isAlphaDisabled={isAlphaDisabled}
      />
    );
  };

  return (
    <div className={containerClasses}>
      {getHexPicker()}
      <div className={`${styles.ctaContainer} flex-row-justify-between`}>
        {getHexInputField()}
        {getEyeDropperPicker()}
      </div>
      {getOnMyDesignSection()}
    </div>
  );
}
